import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WalletSummary, WalletTransaction, TopupRequest, TransactionType } from '@/types/wallet.types';
import { PaginatedResponse } from '@/types';
import { walletService } from '@/services/wallet.service';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { WalletBalanceCard, TransactionList } from '@/components/wallet';

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getTopupStatusBadge = (status: TopupRequest['status']): { label: string; colors: string } => {
  switch (status) {
    case 'pending':
      return { label: 'Pending', colors: 'bg-yellow-100 text-yellow-800' };
    case 'approved':
      return { label: 'Approved', colors: 'bg-green-100 text-green-800' };
    case 'rejected':
      return { label: 'Rejected', colors: 'bg-red-100 text-red-800' };
  }
};

const typeFilterOptions: { value: TransactionType | ''; label: string }[] = [
  { value: '', label: 'All Transactions' },
  { value: 'CREDIT', label: 'Credits Only' },
  { value: 'DEBIT', label: 'Debits Only' },
  { value: 'REFUND', label: 'Refunds Only' },
];

export const WalletDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [summary, setSummary] = useState<WalletSummary | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [topupRequests, setTopupRequests] = useState<TopupRequest[]>([]);
  const [meta, setMeta] = useState<PaginatedResponse<WalletTransaction>['meta'] | null>(null);

  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [isLoadingTopups, setIsLoadingTopups] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState<TransactionType | ''>(
    (searchParams.get('type') as TransactionType) || ''
  );
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  const fetchSummary = useCallback(async () => {
    try {
      setIsLoadingSummary(true);
      const response = await walletService.getSummary();
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching wallet summary:', err);
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      setIsLoadingTransactions(true);
      setError(null);

      const filters = typeFilter ? { type: typeFilter } : undefined;
      const response = await walletService.getTransactions(filters, page, 10);
      setTransactions(response.data?.data ?? []);
      setMeta(response.data?.meta ?? null);
    } catch (err) {
      setError('Failed to load transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [typeFilter, page]);

  const fetchTopupRequests = useCallback(async () => {
    try {
      setIsLoadingTopups(true);
      const response = await walletService.getMyTopupRequests();
      // API interceptor unwraps { success, data } - backend returns { topupRequests: [...] }
      const requestsData = response.data?.topupRequests || [];
      // Show only pending requests
      setTopupRequests(requestsData.filter((r) => r.status === 'pending'));
    } catch (err) {
      console.error('Error fetching topup requests:', err);
    } finally {
      setIsLoadingTopups(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchTopupRequests();
  }, [fetchSummary, fetchTopupRequests]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (typeFilter) params.set('type', typeFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [typeFilter, page, setSearchParams]);

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value as TransactionType | '');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Wallet</h1>
        <p className="text-secondary-600 mt-1">
          Manage your wallet balance and view transaction history
        </p>
      </div>

      {/* Balance and Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Balance Card */}
        <div className="lg:col-span-1">
          <WalletBalanceCard
            balance={summary?.balance || 0}
            isLoading={isLoadingSummary}
          />
        </div>

        {/* Summary Stats */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Credits This Month */}
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 11l5-5m0 0l5 5m-5-5v12"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-secondary-600">Credits This Month</p>
                {isLoadingSummary ? (
                  <div className="h-6 w-20 bg-secondary-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-lg font-semibold text-secondary-900">
                    {formatIndianCurrency(summary?.total_credits_this_month || 0)}
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Debits This Month */}
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 13l-5 5m0 0l-5-5m5 5V6"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-secondary-600">Debits This Month</p>
                {isLoadingSummary ? (
                  <div className="h-6 w-20 bg-secondary-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-lg font-semibold text-secondary-900">
                    {formatIndianCurrency(summary?.total_debits_this_month || 0)}
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Pending Topups */}
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-secondary-600">Pending Topups</p>
                {isLoadingSummary ? (
                  <div className="h-6 w-20 bg-secondary-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-lg font-semibold text-secondary-900">
                    {summary?.pending_topups || 0}
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Pending Topup Requests */}
      {!isLoadingTopups && topupRequests.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Pending Topup Requests
            </h2>
          </div>
          <div className="space-y-3">
            {topupRequests.map((request) => {
              const statusBadge = getTopupStatusBadge(request.status);
              return (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-secondary-900">
                      {formatIndianCurrency(request.amount)}
                    </p>
                    <p className="text-sm text-secondary-500">
                      {request.payment_method} | {formatDate(request.created_at)}
                    </p>
                    {request.payment_reference && (
                      <p className="text-xs text-secondary-400 mt-1">
                        Ref: {request.payment_reference}
                      </p>
                    )}
                  </div>
                  <span
                    className={`px-2.5 py-1 text-sm font-medium rounded-full ${statusBadge.colors}`}
                  >
                    {statusBadge.label}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Transaction History */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-secondary-900">
            Transaction History
          </h2>
          <select
            value={typeFilter}
            onChange={(e) => handleTypeFilterChange(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {typeFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchTransactions}>Try Again</Button>
          </div>
        )}

        {!error && (
          <TransactionList
            transactions={transactions}
            isLoading={isLoadingTransactions}
          />
        )}

        {/* Pagination */}
        {!error && meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-secondary-200 pt-4 mt-4">
            <div className="text-sm text-secondary-600">
              Showing {(meta.page - 1) * meta.limit + 1} to{' '}
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} transactions
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page === meta.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default WalletDashboard;
